import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Link2, Loader2, Plug, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import McpServers, { waarschuw } from "./McpServers";
import { magKoppelen } from "./NextcloudConnection";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { CATALOGUS, alGekoppeld, zoekIn } from "@/lib/koppelcatalogus";

/**
 * W1a: het scherm Koppelingen, als groot venster vanuit het menu (besluit Frank
 * 23 oktober 2026: App.js valt onder de bevroren verbindingscode, dus geen route).
 *
 * Twee tabbladen en een zoekveld. Jouw koppelingen is het bestaande MCP-blok uit
 * Instellingen (`McpServers`), hier met het zoekveld erop; Instellingen zelf blijft
 * ongewijzigd. Ontdekken toont de catalogus, met als eerste Direkto. Koppelen vanuit
 * Ontdekken gaat langs dezelfde route als het formulier, en een kijker krijgt geen knop
 * (de backend weigert hem ook).
 */
export default function KoppelingenVenster({ open, onOpenChange }) {
  const { t } = useTaal();
  const [tab, setTab] = useState("jouw");
  const [zoek, setZoek] = useState("");
  const [servers, setServers] = useState([]);
  const [rol, setRol] = useState("");
  const [bezig, setBezig] = useState("");
  const [tokens, setTokens] = useState({});

  useEffect(() => {
    if (!open || tab !== "ontdekken") return;
    axios.get(`${API}/mcp-servers`).then((r) => setServers(Array.isArray(r.data) ? r.data : [])).catch(() => setServers([]));
    axios.get(`${API}/organisatie`).then((o) => setRol(o.data?.mijn_rol || "")).catch(() => setRol(""));
  }, [open, tab]);

  const koppel = async (item) => {
    // W4: een koppeling die een token vraagt, koppelt pas met een ingevuld token.
    const sleutel = item.sleutelNodig ? (tokens[item.id] || "").trim() : "";
    if (item.sleutelNodig && !sleutel) {
      toast.error(t("Vul eerst je token in."));
      return;
    }
    setBezig(item.id);
    try {
      const r = await axios.put(`${API}/mcp-servers`, { naam: item.naam, adres: item.adres, sleutel });
      setTokens((v) => ({ ...v, [item.id]: "" }));
      toast.success(t("{naam} gekoppeld", { naam: item.naam }));
      waarschuw(r?.data, t);
      setTab("jouw");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setBezig("");
    }
  };

  const gevonden = zoekIn(CATALOGUS, zoek, [(i) => i.naam, (i) => i.adres, (i) => t(i.omschrijving)]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[85dvh] flex flex-col" data-testid="koppelingen-venster">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plug className="h-4 w-4" />{t("menu.koppelingen")}</DialogTitle>
          <DialogDescription>{t("Servers die gereedschap geven aan de chat en aan je agents. Lezen en zoeken gaat direct; bestellen, een link maken of iets aanvragen wacht eerst op je bevestiging.")}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={zoek} onChange={(e) => setZoek(e.target.value)} className="pl-8"
                 placeholder={t("Zoek een koppeling")} aria-label={t("Zoek een koppeling")}
                 data-testid="koppelingen-zoek" />
        </div>
        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="self-start">
            <TabsTrigger value="jouw" data-testid="koppelingen-tab-jouw">{t("Jouw koppelingen")}</TabsTrigger>
            <TabsTrigger value="ontdekken" data-testid="koppelingen-tab-ontdekken">{t("Ontdekken")}</TabsTrigger>
          </TabsList>
          <TabsContent value="jouw" className="flex-1 min-h-0 overflow-y-auto">
            <McpServers zoek={zoek} />
          </TabsContent>
          <TabsContent value="ontdekken" className="flex-1 min-h-0 overflow-y-auto">
            {gevonden.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="ontdekken-leeg">
                {t("Geen koppeling met \"{zoek}\"", { zoek })}
              </p>
            ) : (
              <ul className="space-y-2" data-testid="ontdekken-lijst">
                {gevonden.map((item) => {
                  const gekoppeld = alGekoppeld(item.adres, servers);
                  return (
                    <li key={item.id} className="border rounded-md p-3 flex items-start gap-3" data-testid="ontdekken-rij">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{item.naam}</p>
                        <p className="text-xs text-muted-foreground">{t(item.omschrijving)}</p>
                        <p className="text-xs text-muted-foreground break-all">{item.adres}</p>
                        {item.sleutelNodig && !gekoppeld && magKoppelen(rol) && (
                          <Input type="password" autoComplete="off" value={tokens[item.id] || ""}
                                 onChange={(e) => setTokens((v) => ({ ...v, [item.id]: e.target.value }))}
                                 placeholder={t("Je token")} aria-label={t("Token voor {naam}", { naam: item.naam })}
                                 className="mt-2 h-8 text-xs" data-testid={`ontdekken-token-${item.id}`} />
                        )}
                      </div>
                      {gekoppeld ? (
                        <span className="text-xs flex items-center gap-1" data-testid="ontdekken-gekoppeld">
                          <Check className="h-3 w-3" />{t("Gekoppeld")}
                        </span>
                      ) : magKoppelen(rol) && (
                        <Button size="sm" variant="outline" disabled={!!bezig} onClick={() => koppel(item)}
                                data-testid="ontdekken-koppel">
                          {bezig === item.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Link2 className="h-3 w-3 mr-1" />}
                          {t("Koppelen")}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
